import { getDict } from "../i18n";

/**
 * App-wide toasts. Call from anywhere — components, lib code, API helpers:
 *   toast.success("Saved");  toast.error(err);  toast("Plain message");
 * <Toaster /> (mounted once in main.tsx) renders them.
 */

export type ToastTone = "default" | "success" | "error" | "warning" | "info";

export interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
}

const DURATION_MS = { default: 4000, success: 5000, info: 5000, warning: 6000, error: 7000 };

let toasts: ToastItem[] = [];
const listeners = new Set<() => void>();

const emit = (next: ToastItem[]) => {
  toasts = next;
  listeners.forEach((l) => l());
};

export function subscribeToasts(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const getToasts = () => toasts;

export function dismissToast(id: number) {
  emit(toasts.filter((t) => t.id !== id));
}

function show(tone: ToastTone, message: string) {
  const id = Date.now() + Math.random();
  emit([...toasts, { id, tone, message }]);
  setTimeout(() => dismissToast(id), DURATION_MS[tone]);
  return id;
}

/** Accepts a thrown value too, so `catch (err) { toast.error(err) }` just works. */
const errorMessage = (err: unknown, fallback?: string) =>
  typeof err === "string"
    ? err
    : err instanceof Error
      ? err.message
      : (fallback ?? getDict().common.somethingWrong);

export const toast = Object.assign((message: string) => show("default", message), {
  success: (message: string) => show("success", message),
  info: (message: string) => show("info", message),
  warning: (message: string) => show("warning", message),
  error: (err: unknown, fallback?: string) => show("error", errorMessage(err, fallback)),
  dismiss: dismissToast,
});
